import type { UseDocumentsReturn } from "./hooks/useDocuments";

export const LinkType = {
  Project: "Project",
  Customer: "Customer",
  Meeting: "Meeting",
  MeetingAgendaItem: "MeetingAgendaItem"
} as const;

export type LinkTypeValue = (typeof LinkType)[keyof typeof LinkType];

export interface DocumentDto {
  documentId: string;
  projectId: number;
  customerId: number;
  filename: string;
  fileSize?: number;
  mimeType?: string;
  isPublic: boolean;
  dateAdded: string;
  lastModified?: string;
  comments?: string;
  downloadUrl?: string;
  tags?: string[];
}

export interface AddDocumentRequest {
  file: File;
  filename: string;
  isPublic: boolean;
  comments?: string;
  tags?: string[];
}

export interface EditDocumentRequest {
  documentId: string;
  filename: string;
  isPublic: boolean;
  comments?: string;
  tags?: string[];
}

export interface DeleteDocumentRequest {
  documentId: string;
}

export interface AddDocumentResponse {
  success: boolean;
  message?: string;
  data?: DocumentDto;
}

export interface EditDocumentResponse {
  success: boolean;
  message?: string;
  data?: DocumentDto;
}

export interface DeleteDocumentResponse {
  success: boolean;
  message?: string;
}

export interface GetDocumentsRequest {
  linkType: string;
  linkTargetId: number;
  filter?: DocumentFilterDto;
  searchTerm?: string;
  sortField?: DocumentSortFieldDto;
  sortDirection?: SortDirectionDto;
  page?: number;
  pageSize?: number;
}

export interface PageDto<T> {
  data: T[];
  pageNumber: number;
  pageSize: number;
  total: number;
}

export interface GetDocumentsResponse {
  success: boolean;
  message?: string;
  data?: {
    items: DocumentDto[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export enum DocumentSortFieldDto {
  Filename = "Filename",
  DateAdded = "DateAdded",
  FileSize = "FileSize",
  UploadedBy = "UploadedBy"
}

export enum SortDirectionDto {
  Asc = "Asc",
  Desc = "Desc"
}

export enum DocumentFilterDto {
  All = "All",
  Public = "Public",
  Private = "Private"
}

export interface AdminUserDto {
  userId: string;
  name: string | null;
  email: string;
}

export interface DocumentFormData {
  filename: string;
  comments: string;
  isPublic: boolean;
}

export interface DocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  document?: DocumentDto | null;
  projectId: number;
  onSave: (data: AddDocumentRequest | EditDocumentRequest) => Promise<void>;
  mode: "create" | "edit";
  saveError?: string | null;
}

export interface DocumentsTableProps {
  documents: UseDocumentsReturn;
  onDocumentClick?: (document: DocumentDto) => void;
  onAddDocument?: () => void;
}

export interface DocumentTableColumn {
  id: string;
  label: string;
  sortable: boolean;
  width?: number;
}

export interface PaginationConfig {
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
}

export interface PublicDocumentDto {
  id: string;
  projectId: number;
  fileName: string;
  displayName?: string;
  mimeType?: string;
  sizeBytes?: number;
  uploadedUtc: string;
  downloadUrl?: string;
}

export interface PublicDocumentsResponse {
  data: PublicDocumentDto[];
  pageNumber: number;
  pageSize: number;
  total: number;
}
